import React, { useState, useEffect, useRef, useMemo } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./CreateAppointmentModal.module.css";
import { 
  useCreateAppointmentFastMutation,
  useGetAllRelevantTherapistForUserQuery,
  useGetTherapistMonthlyAvailabilityQuery
} from "../../../../services/api/user.api";
import { useAppSelector } from "../../../../services/hooks";
import { userSelector } from "../../../../services/selectors/user.selectors";
import { IConflict, ITherapistAvailability, IDailySchedule } from "../../types/appointments.types";

interface ICreateAppointmentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialDate: string;
  initialTime?: string;
  initialTherapistId?: number; // Optional: If opening from a specific therapist context
}

const timeToMinutes = (time: string) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const checkOverlapWithBusy = (start: string, end: string, busySlots: {start: string, end: string}[]) => {
  if (!start || !end || !busySlots?.length) return false;
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (s >= e) return false; // Invalid time range isn't an overlap

  return busySlots.some(slot => {
      const busyStart = timeToMinutes(slot.start);
      const busyEnd = timeToMinutes(slot.end);
      // Standard overlap check: (StartA < EndB) and (EndA > StartB)
      return (s < busyEnd && e > busyStart);
  });
};

const formatDate = (dateStr: string): string => {
  try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
  } catch {
      return dateStr;
  }
};

const getPositionStyle = (start: string, end: string) => {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);
  
  const dayStart = 7 * 60; // 07:00
  const dayEnd = 23 * 60; // 23:00 - Extended a bit
  const totalMin = dayEnd - dayStart; 

  // Clamp values
  const s = Math.max(startMin, dayStart);
  const e = Math.min(endMin, dayEnd);
  
  if (s >= e) return { display: 'none' };

  const left = ((s - dayStart) / totalMin) * 100;
  const width = ((e - s) / totalMin) * 100;

  return { 
      position: 'absolute' as 'absolute',
      left: `${left}%`, 
      width: `${width}%`,
      height: '100%' 
  };
};

const therapistShownInAppointmentDropdown = (t: { StatusId?: number; statusId?: number }) =>
  (t.StatusId ?? t.statusId ?? 1) !== 0;

const roundTo15Minutes = (time: string) => {
  if (!time) return time;
  const [h, m] = time.split(":").map(Number);
  const roundedM = Math.round(m / 15) * 15;
  const dateObj = new Date();
  dateObj.setHours(h, roundedM);
  return dateObj.toTimeString().slice(0, 5);
};

const CreateAppointmentModal: React.FC<ICreateAppointmentModalProps> = ({
  onClose,
  onSuccess,
  initialDate,
  initialTime = "09:00",
  initialTherapistId
}) => {
  const isRtl = i18n.language === "he";
  const currentUser = useAppSelector(userSelector);
  
  // API hooks
  const [postAppointment, { isLoading: isCreating }] = useCreateAppointmentFastMutation();
  
  // Get Therapists for the current user
  const { data: therapists, isLoading: isLoadingTherapists } = useGetAllRelevantTherapistForUserQuery({
    Type: 1,
    UsersGuid: currentUser?.guid || ""
  }, {
    skip: !currentUser?.guid
  });

  // Form state
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(initialTime);
  const [endTime, setEndTime] = useState(() => {
    const [h, m] = initialTime.split(":").map(Number);
    if (isNaN(h)) return "09:45";
    const dateObj = new Date();
    dateObj.setHours(h, m + 45);
    return dateObj.toTimeString().slice(0, 5);
  });
  
  // Selected Therapist
  const [selectedTherapistId, setSelectedTherapistId] = useState<number>(initialTherapistId || 0);
  const [selectedTherapistName, setSelectedTherapistName] = useState("");
  const [selectedTherapistGuid, setSelectedTherapistGuid] = useState("");

  // Initialize selected therapist if passed or if only one available
  useEffect(() => {
    if (therapists) {
      const activeTherapists = therapists.filter(
        (t) =>
          therapistShownInAppointmentDropdown(t) &&
          t.allowPatientBooking !== false &&
          t.allowPatientBooking !== null
      );
      
      if (initialTherapistId) {
        const t = activeTherapists.find(t => t.id === String(initialTherapistId));
        if (t) {
          setSelectedTherapistId(Number(t.id));
          setSelectedTherapistName(t.fullName || t.name || "");
          setSelectedTherapistGuid(t.guid);
        }
      } else if (activeTherapists.length === 1) {
        const t = activeTherapists[0];
        setSelectedTherapistId(Number(t.id));
        setSelectedTherapistName(t.fullName || t.name || "");
        setSelectedTherapistGuid(t.guid);
      }
    }
  }, [therapists, initialTherapistId]);

  const [mediaId, setMediaId] = useState(1);
  const [comments, setComments] = useState("");
  const [repeatCount, setRepeatCount] = useState(1);
  const [appointmentType, setAppointmentType] = useState<'treatment' | 'other'>('treatment');

  // UI state
  const [therapistSearch, setTherapistSearch] = useState("");
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  const [showStartTimeDropdown, setShowStartTimeDropdown] = useState(false);
  const [showEndTimeDropdown, setShowEndTimeDropdown] = useState(false);
  

  // Refs
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);
  const startTimeDropdownRef = useRef<HTMLDivElement>(null);
  const endTimeDropdownRef = useRef<HTMLDivElement>(null);

  // Time slots (15-min intervals)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let h = 7; h <= 23; h++) {
      for (let m = 0; m < 60; m += 15) {
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  }, []);

  // Constants that use translation
  const dayNames = [
    t("SUNDAY"), t("MONDAY"), t("TUESDAY"), t("WEDNESDAY"), t("THURSDAY"), t("FRIDAY"), t("SATURDAY")
  ];
  const getDayName = (dayId: number) => dayNames[dayId - 1] || "";

  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") },
    { id: 2, name: t("MEDIA_VIDEO") },
    { id: 3, name: t("MEDIA_PHONE") },
    { id: 4, name: t("MEDIA_CLINIC") },
  ];

  // Filtered Therapists
  const searchedTherapists = useMemo(() => {
    if (!therapists) return [];
    
    let filtered = therapists.filter(
      (t) =>
        therapistShownInAppointmentDropdown(t) &&
        t.allowPatientBooking !== false &&
        t.allowPatientBooking !== null
    );
    
    if (!therapistSearch) return filtered;
    
    const query = therapistSearch.toLowerCase();
    return filtered.filter(t => 
      (t.fullName || t.name || "").toLowerCase().includes(query) || 
      (t.phone && t.phone.includes(query))
    );
  }, [therapists, therapistSearch]);



  // Monthly Schedule Fetch
  const [queryMonth, setQueryMonth] = useState(() => {
    const d = new Date(initialDate || new Date().toISOString());
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  });

  useEffect(() => {
    if (date) {
        const d = new Date(date);
        // JS Date parsing of "yyyy-mm-dd" is UTC, but extraction methods use local time unless using getUTC...
        // However, "yyyy-mm-dd" string input is usually stable. 
        // Better to parse the string split to avoid timezone off-by-one errors.
        const [y, m] = date.split('-').map(Number);
        if (y && m) {
            setQueryMonth({ month: m, year: y });
        }
    }
  }, [date]);

  const { data: monthlySchedule, isLoading: isLoadingSchedule } = useGetTherapistMonthlyAvailabilityQuery(
    {
      therapistsGuid: selectedTherapistGuid,
      month: queryMonth.month,
      year: queryMonth.year
    },
    {
      skip: !selectedTherapistId || appointmentType !== 'treatment'
    }
  );

  // Normalize Backend Data
  const dailySchedule = useMemo(() => {
    if (!monthlySchedule?.days || !date) return null;
    let dayData = monthlySchedule.days[date] as any;
    
    if (!dayData) return undefined;

    // Handle PascalCase from Backend if present
    if (dayData.IsWorkingDay !== undefined) {
        // Backend returns WorkingHours as array now, but handle single object for backward compat if needed
        let wh: { start: string, end: string }[] = [];
        if (Array.isArray(dayData.WorkingHours)) {
             wh = dayData.WorkingHours.map((w: any) => ({ start: w.Start, end: w.End }));
        } else if (dayData.WorkingHours) {
             wh = [{ start: dayData.WorkingHours.Start, end: dayData.WorkingHours.End }];
        }

        // Sort working hours by start time
        wh.sort((a,b) => timeToMinutes(a.start) - timeToMinutes(b.start));

        return {
            isWorkingDay: dayData.IsWorkingDay,
            workingHours: wh.length > 0 ? wh : undefined,
            busySlots: dayData.BusySlots ? dayData.BusySlots.map((s: any) => ({
                start: s.Start,
                end: s.End
            })) : []
        } as IDailySchedule;
    }

    return dayData as IDailySchedule;
  }, [monthlySchedule, date]);


  const isTimeOverlapping = useMemo(() => {
      return checkOverlapWithBusy(startTime, endTime, dailySchedule?.busySlots || []);
  }, [startTime, endTime, dailySchedule]);

  const isTimeInWorkingHours = useMemo(() => {
      if (!dailySchedule?.isWorkingDay || !dailySchedule.workingHours?.length) return false;
      const startMin = timeToMinutes(startTime);
      const endMin = timeToMinutes(endTime);
      
      // Check if selected range is contained within ANY single working hour block
      return dailySchedule.workingHours.some(wh => {
          const whStart = timeToMinutes(wh.start);
          const whEnd = timeToMinutes(wh.end);
          return startMin >= whStart && endMin <= whEnd;
      });
  }, [startTime, endTime, dailySchedule]);

  const isNotAvailable = (dailySchedule && !dailySchedule.isWorkingDay) || (dailySchedule?.isWorkingDay && !isTimeInWorkingHours);

  const isStartBeforeEnd = timeToMinutes(startTime) < timeToMinutes(endTime);

  // Adjust save disabled logic
  const isValid = date && startTime && endTime && 
    (appointmentType === 'treatment' ? selectedTherapistId : comments.trim().length > 0);
  const isSaveDisabled = !isValid || !isStartBeforeEnd || isTimeOverlapping || isCreating || (appointmentType === 'treatment' && isNotAvailable);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setShowMediaDropdown(false);
      }
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setShowTherapistDropdown(false);
      }
      if (startTimeDropdownRef.current && !startTimeDropdownRef.current.contains(event.target as Node)) {
        setShowStartTimeDropdown(false);
      }
      if (endTimeDropdownRef.current && !endTimeDropdownRef.current.contains(event.target as Node)) {
        setShowEndTimeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCreate = async () => {
    if (isSaveDisabled || !currentUser?.id) return;
    
    // For 'other' type appointments, maybe we need a self-appointment or just a block? 
    // Assuming 'other' also relates to a therapist or just the user's calendar.
    
    try {
      await postAppointment({
        appointment: {
            AppointmentDate: new Date(date),
            StartTime: startTime,
            EndTime: endTime,
            TherapistsId: appointmentType === 'treatment' ? selectedTherapistId : null,
            TherapistsGuid: appointmentType === 'treatment' ? selectedTherapistGuid : null,
            UsersId: Number(currentUser.id),
            UsersGuid: currentUser.guid,
            MediaId: appointmentType === 'treatment' ? mediaId : null,
            Comments: comments,
            CompanyId: currentUser.companyId || 1,
            StatusId: 1, // Pending/New
            Active: 1 // Active
        } as any,
        repeatCount
      }).unwrap();
      onSuccess();
    } catch (error) {
      console.error("Failed to create appointment:", error);
    }
  };



  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span>➕</span>
            <span>{t("APPOINTMENTS_NEW_TITLE")}</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {/* Appointment Type Toggle (optional for client, mostly treatment) */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setAppointmentType('treatment')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'treatment' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'treatment' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'treatment' ? 600 : 400,
                color: appointmentType === 'treatment' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("TREATMENT_APPOINTMENT") || "טיפול"}
            </button>
            <button
              onClick={() => setAppointmentType('other')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'other' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'other' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'other' ? 600 : 400,
                color: appointmentType === 'other' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("OTHER") || "אחר"}
            </button>
          </div>

          {/* Therapist Selection (instead of Patient Selection) */}
          {appointmentType === 'treatment' ? (
          <div className={styles.formGroup} ref={therapistDropdownRef}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>👨‍⚕️</span>
              {t("APPOINTMENTS_THERAPIST")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${showTherapistDropdown ? styles.customSelectActive : ''}`}
                onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {selectedTherapistId ? `👨‍⚕️ ${selectedTherapistName}` : t("SELECT_THERAPIST") || "בחר מטפל"}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showTherapistDropdown && (
                <div className={styles.customSelectDropdown}>
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      className={styles.input}
                      style={{ minHeight: '36px', padding: '8px 12px', fontSize: '14px' }}
                      placeholder={t("SEARCH") || "חיפוש..."}
                      value={therapistSearch}
                      onChange={(e) => setTherapistSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {isLoadingTherapists ? (
                      <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                        <span className={styles.optionText}>{t("LOADING")}...</span>
                      </div>
                    ) : searchedTherapists && searchedTherapists.length > 0 ? (
                      searchedTherapists.map((th) => (
                        <div
                          key={th.id}
                          className={`${styles.dropdownOption} ${selectedTherapistId === Number(th.id) ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setSelectedTherapistId(Number(th.id));
                            setSelectedTherapistGuid(th.guid);
                            setSelectedTherapistName(th.fullName || th.name);
                            setShowTherapistDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{th.fullName || th.name}</span>
                          {selectedTherapistId === Number(th.id) && <span className={styles.checkmark}>✓</span>}
                        </div>
                      ))
                    ) : (
                      <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                        <span className={styles.optionText}>{t("NO_RESULTS_FOUND")}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          ) : null}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📅</span>
              {t("APPOINTMENTS_DATE")}
            </label>
            <input
              type="date"
              className={styles.input}
              value={formatDate(date)}
              onChange={(e) => setDate(e.target.value)}
            />
            {/* Daily Schedule Timeline (Only for treatment) */}
            {appointmentType === 'treatment' && (
              <>
                {dailySchedule ? (
                  <div dir="ltr" style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '12px', color: '#4b5563', fontWeight: 500, direction: isRtl ? 'rtl' : 'ltr' }}>
                          <span>
                            {t("THERAPIST_AVAILABILITY") || "זמינות מטפל"}:{" "}
                            {dailySchedule.isWorkingDay 
                              ? (dailySchedule.workingHours?.length 
                                  ? dailySchedule.workingHours.map(wh => `${wh.start} - ${wh.end}`).join(", ") 
                                  : (t("AVAILABLE") || "זמין")) 
                              : (t("NOT_WORKING_DAY") || "לא זמין/יום חופש")
                            }
                          </span>
                      </div>

                      {dailySchedule.isWorkingDay && dailySchedule.workingHours?.map((wh, whIdx) => {
                          // Calculate positions relative to this specific working hour range
                          const rangeStart = timeToMinutes(wh.start);
                          const rangeEnd = timeToMinutes(wh.end);
                          const rangeDuration = rangeEnd - rangeStart;

                          return (rangeDuration > 0) ? (
                            <div key={whIdx} style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px', fontWeight: 600 }}>{wh.start} - {wh.end}</div>
                              {/* Timeline Bar */}
                              <div style={{ position: 'relative', height: '24px', backgroundColor: '#dcfce7', borderRadius: '4px', overflow: 'hidden', border: '1px solid #bbf7d0' }}>
                                  
                                  {/* Busy Slots Overlay */}
                                  {dailySchedule.busySlots?.map((slot, idx) => {
                                      const sMin = timeToMinutes(slot.start);
                                      const eMin = timeToMinutes(slot.end);

                                      // Check if slot overlaps with this WH range
                                      if (eMin <= rangeStart || sMin >= rangeEnd) return null;

                                      // Clamp to range
                                      const clampedStart = Math.max(sMin, rangeStart);
                                      const clampedEnd = Math.min(eMin, rangeEnd);
                                      
                                      const left = ((clampedStart - rangeStart) / rangeDuration) * 100;
                                      const width = ((clampedEnd - clampedStart) / rangeDuration) * 100;

                                      return (
                                          <div 
                                              key={`busy-${idx}`}
                                              style={{ 
                                                  position: 'absolute',
                                                  left: `${left}%`,
                                                  width: `${width}%`,
                                                  height: '100%',
                                                  backgroundColor: '#ef4444', // red
                                                  zIndex: 2,
                                                  opacity: 0.9,
                                                  borderRadius: '1px',
                                                  borderLeft: '1px solid #b91c1c',
                                                  borderRight: '1px solid #b91c1c'
                                              }}
                                              title={`${t("BUSY") || "עסוק"}: ${slot.start} - ${slot.end}`} 
                                          />
                                      );
                                  })}

                                  {/* Selected Range Visualizer */}
                                  {(() => {
                                      const sMin = timeToMinutes(startTime);
                                      const eMin = timeToMinutes(endTime);
                                      if (eMin <= rangeStart || sMin >= rangeEnd) return null;

                                      const clampedStart = Math.max(sMin, rangeStart);
                                      const clampedEnd = Math.min(eMin, rangeEnd);
                                      const left = ((clampedStart - rangeStart) / rangeDuration) * 100;
                                      const width = ((clampedEnd - clampedStart) / rangeDuration) * 100;

                                      return (
                                          <div 
                                              style={{
                                                  position: 'absolute',
                                                  left: `${left}%`,
                                                  width: `${width}%`,
                                                  height: '100%',
                                                  border: '2px solid #8b5cf6',
                                                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                                                  zIndex: 3,
                                                  pointerEvents: 'none',
                                                  borderRadius: '2px'
                                              }}
                                              title={t("YOUR_SELECTION") || "הבחירה שלך"}
                                          />
                                      );
                                  })()}
                              </div>
                              
                              {/* Range Labels */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginTop: '2px', padding: '0 2px' }}>
                                  <span>{wh.start}</span>
                                  <span>{wh.end}</span>
                              </div>
                            </div>
                          ) : null;
                      })}

                      <div style={{ display: 'flex', gap: '10px', marginTop: '6px', fontSize: '10px', direction: isRtl ? 'rtl' : 'ltr', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#dcfce7', border: '1px solid #bbf7d0' }}></div>
                            <span>{t("AVAILABLE") || "פנוי"}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ width: '8px', height: '8px', backgroundColor: '#ef4444', border: '1px solid #b91c1c' }}></div>
                            <span>{t("BUSY") || "תפוס"}</span>
                        </div>
                      
                      </div>
                  </div>
                ) : isLoadingSchedule ? (
                  <div style={{ fontSize: '11px', color: '#9ca3af', padding: '8px 0' }}>{t("LOADING_AVAILABILITY") || "טוען זמינות..."}</div>
                ) : null}
              </>
            )}
          </div>

          <div className={styles.formRow} style={{ display: 'flex', gap: '10px' }}>
            <div className={styles.inputGroup} style={{ flex: 1 }} ref={startTimeDropdownRef}>
              <label className={styles.label}>{t("START_TIME")}</label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showStartTimeDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowStartTimeDropdown(!showStartTimeDropdown)}
                  style={(isTimeOverlapping || !isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                >
                  <span className={styles.customSelectValue}>{startTime}</span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showStartTimeDropdown && (
                  <div className={styles.customSelectDropdown} style={{ maxHeight: '200px' }}>
                    <div className={styles.dropdownOptions}>
                      {timeSlots.map((slot) => (
                        <div
                          key={slot}
                          className={`${styles.dropdownOption} ${startTime === slot ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setStartTime(slot);
                            const [h, m] = slot.split(":").map(Number);
                            const d = new Date();
                            d.setHours(h, m + 45);
                            const hh = d.getHours().toString().padStart(2, '0');
                            const mm = d.getMinutes().toString().padStart(2, '0');
                            setEndTime(`${hh}:${mm}`);
                            setShowStartTimeDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{slot}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.inputGroup} style={{ flex: 1 }} ref={endTimeDropdownRef}>
              <label className={styles.label}>{t("END_TIME")}</label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showEndTimeDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowEndTimeDropdown(!showEndTimeDropdown)}
                  style={(isTimeOverlapping || !isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                >
                  <span className={styles.customSelectValue}>{endTime}</span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showEndTimeDropdown && (
                  <div className={styles.customSelectDropdown} style={{ maxHeight: '200px' }}>
                    <div className={styles.dropdownOptions}>
                      {timeSlots.map((slot) => (
                        <div
                          key={slot}
                          className={`${styles.dropdownOption} ${endTime === slot ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setEndTime(slot);
                            setShowEndTimeDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{slot}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {!isStartBeforeEnd && startTime && endTime && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("START_TIME_BEFORE_END_TIME_ERROR") || "שעת הסיום חייבת להיות אחרי שעת ההתחלה"}
              </div>
          )}
          {isTimeOverlapping && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("TIME_SLOT_BUSY_ERROR") || "השעות שנבחרו חופפות לתור קיים"}
              </div>
          )}
          {isNotAvailable && appointmentType === 'treatment' && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("TIME_SLOT_NOT_AVAILABLE_ERROR") || t("NOT_WORKING_DAY") || "המטפל לא זמין בשעות אלו"}
              </div>
          )}



          {/* Appointment Type Specific Fields */}
          {appointmentType === 'treatment' && (
            <>
           {/* Repeat Count - Commented out
           <div className={styles.formGroup}>...</div>
           */}

          {/* Media Field */}
          <div className={styles.formGroup} ref={mediaDropdownRef}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📍</span>
              {t("APPOINTMENTS_MEDIA")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${showMediaDropdown ? styles.customSelectActive : ''}`}
                onClick={() => setShowMediaDropdown(!showMediaDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {mediaTypes.find(m => m.id === mediaId)?.name || t("SELECT_MEDIA")}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showMediaDropdown && (
                <div className={styles.customSelectDropdown}>
                  <div className={styles.dropdownOptions}>
                    {mediaTypes.map((media) => (
                      <div
                        key={media.id}
                        className={`${styles.dropdownOption} ${mediaId === media.id ? styles.dropdownOptionSelected : ''}`}
                        onClick={() => {
                          setMediaId(media.id);
                          setShowMediaDropdown(false);
                        }}
                      >
                        <span className={styles.optionText}>{media.name}</span>
                        {mediaId === media.id && <span className={styles.checkmark}>✓</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
            </>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📝</span>
              {appointmentType === 'treatment' ? t("APPOINTMENTS_NOTES") : (t("MESSAGE_CONTENT") || "תוכן")}
            </label>
            <textarea
              className={styles.textarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t("APPOINTMENTS_NOTES")}
            />
          </div>

        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} style={{marginRight: '10px'}}>{t("APPOINTMENTS_CLOSE")}</button>
          <button
            className={styles.saveBtn}
            onClick={handleCreate}
            disabled={isSaveDisabled}
            style={isSaveDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            {isCreating ? t("SAVING") : t("APPOINTMENTS_SAVE")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAppointmentModal;
